/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { publicHttpClient } from '@ahm/api-wrappers-core';
import { shouldAttachBearer } from './auth-interceptor-shared';
import { ensureValidToken, triggerRefresh } from './client-refresh';
import { getAuthToken } from './get-token';
import { terminalLogout } from './terminal-logout';

let installed = false;

/**
 * Interceptor cho WEB LOGIN THƯỜNG (OAuth callback set cả `token` + `refresh_token`).
 *
 * ⚠️ KHÔNG dùng cho webinapp — webinapp login bằng token trên URL nên KHÔNG có
 * refresh_token, refresh chain ở đây sẽ luôn fail + xoá cookie → logout oan.
 * Webinapp dùng `installWebinappAuthInterceptor` (webinapp-auth-interceptor.ts).
 * `new-auth-provider` chọn đúng 1 interceptor để cài theo loại session.
 *
 * 2-tier refresh wrapper (tier 1 pre-emptive + tier 2 reactive).
 * Doc reference: refesh-token.md §4 Trigger points.
 *
 * Request interceptor:
 *  - Tier 1: ensureValidToken — nếu exp < 30s, refresh trước, sau đó attach Bearer
 *  - Idempotent: không override Authorization header tự set
 *
 * Response interceptor:
 *  - Tier 2: 401 + chưa retry → triggerRefresh → retry request 1 lần
 *  - Refresh fail → clear cookies + store + cache → propagate 401
 */
export function installAuthAxiosInterceptor() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  publicHttpClient.interceptors.request.use(async (config) => {
    if (
      config.headers &&
      !config.headers.Authorization &&
      shouldAttachBearer(config.url)
    ) {
      const token = await ensureValidToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  publicHttpClient.interceptors.response.use(
    (res) => res,
    async (error) => {
      const status = error?.response?.status;
      const original = error?.config;
      if (
        status !== 401 ||
        !original ||
        original._retry ||
        !shouldAttachBearer(original.url)
      ) {
        return Promise.reject(error);
      }
      original._retry = true;

      const ok = await triggerRefresh();
      if (!ok) {
        // Refresh fail (refresh_token cũng invalid/missing) → terminal logout
        terminalLogout();
        return Promise.reject(error);
      }

      const newToken = getAuthToken();
      if (newToken && original.headers) {
        original.headers.Authorization = `Bearer ${newToken}`;
      }
      return publicHttpClient.request(original);
    }
  );
}
